import { useNavigate, useSearch } from '@tanstack/react-router';
import { ChevronLeft, ChevronRight, RefreshCw, Plus } from 'lucide-react';
import { useEffect } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAnalysisRuns } from '@/hooks/useAnalysis';
import { analysisSearchSchema } from '@/lib/route-search';
import { cn } from '@/lib/utils';

const STATUS_BADGE_VARIANT = {
  pending: 'secondary',
  running: 'default',
  completed: 'outline',
  failed: 'destructive',
} as const;

export default function AnalysisRunsTable() {
  const navigate = useNavigate({ from: '/analysis' });
  const routeSearch = useSearch({ from: '/analysis' });
  const page = routeSearch.page;
  const statusFilter = routeSearch.status;
  const limit = 20;

  const { data, isLoading, error, refetch, isRefetching } = useAnalysisRuns(
    page,
    limit,
    statusFilter || undefined
  );

  // Auto-refetch when active runs exist
  useEffect(() => {
    const hasActiveRuns = data?.some(
      (r) => r.status === 'pending' || r.status === 'running'
    );
    if (!hasActiveRuns) return;

    const interval = setInterval(() => refetch(), 5000);
    return () => clearInterval(interval);
  }, [data, refetch]);

  const hasNextPage = data?.length === limit;

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-destructive">
            Error Loading Analysis Runs
          </CardTitle>
          <CardDescription>
            {error instanceof Error
              ? error.message
              : 'An unknown error occurred'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => refetch()} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>Analysis Runs</CardTitle>
              <CardDescription>
                Batch LLM analysis of chat messages
              </CardDescription>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Select
                value={statusFilter}
                onChange={(e) => {
                  navigate({
                    search: {
                      page: 1,
                      status: analysisSearchSchema.shape.status.parse(
                        e.target.value
                      ),
                    },
                  });
                }}
                className="w-36"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="running">Running</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
              </Select>
              <Button
                onClick={() => refetch()}
                variant="outline"
                size="sm"
                disabled={isRefetching}
              >
                <RefreshCw
                  className={cn('h-4 w-4 mr-2', isRefetching && 'animate-spin')}
                />
                Refresh
              </Button>
              <Button
                size="sm"
                onClick={() =>
                  navigate({
                    to: '/analysis/new',
                    search: { returnTo: routeSearch },
                  })
                }
              >
                <Plus className="h-4 w-4 mr-2" />
                New Analysis
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Target</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Progress</TableHead>
                      <TableHead>Model</TableHead>
                      <TableHead>Created By</TableHead>
                      <TableHead>Created At</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data?.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          className="text-center py-8 text-muted-foreground"
                        >
                          No analysis runs found
                        </TableCell>
                      </TableRow>
                    ) : (
                      data?.map((run) => (
                        <TableRow
                          key={run.id}
                          className="cursor-pointer"
                          onClick={() =>
                            navigate({
                              to: '/analysis/$runId',
                              params: { runId: run.id },
                              search: {
                                returnTo: routeSearch,
                                resultsPage: 1,
                              },
                            })
                          }
                        >
                          <TableCell className="font-medium">
                            {run.name}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {run.target_type === 'messages' &&
                              run.filters.message_mode === 'pairwise'
                                ? 'message pairs'
                                : run.target_type}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={STATUS_BADGE_VARIANT[run.status]}>
                              {run.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {run.processed_messages} / {run.total_messages}
                          </TableCell>
                          <TableCell className="text-sm">{run.model}</TableCell>
                          <TableCell className="text-sm">
                            {run.created_by}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatDate(run.created_at)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-muted-foreground">Page {page}</p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      navigate({
                        search: {
                          ...routeSearch,
                          page: Math.max(1, page - 1),
                        },
                      })
                    }
                    disabled={page === 1}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      navigate({
                        search: { ...routeSearch, page: page + 1 },
                      })
                    }
                    disabled={!hasNextPage}
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
